import { MATCHDAY_COUNT, RESET_CUTOFF_MATCHDAY, SWAP_MATCHDAY } from '@/constants';

export const RULES_DURING_TOURNAMENT_ANCHOR = 'during-the-tournament';

export const TOURNAMENT_RULES_INTRO =
  'Once the World Cup starts your list locks\n\n' +
  'If you didn’t make a list before the opening game you can still make a list! You can join as a latecomer. This means you’ll only collect points from when you made your list and you’ll be tagged as a latecomer in any leagues you’re in.\n\n' +
  'If you already have a list you can reset it during the Group Stage. But you will have all previously accumulated points erased and you’ll be labelled as a latecomer';

export type TournamentFaqItem = { question: string; answer: string };

export const TOURNAMENT_RULES_FAQ: TournamentFaqItem[] = [
  {
    question: 'When do I get my points?',
    answer:
      'Points update when a Matchday is settled. They do not tick up goal-by-goal during live play. Your total score is cumulative through the last settled Matchday. The number in brackets, for example (+18), is how much you gained on the most recent settled Matchday. Before the first settlement, or between settlements, your score may show 0 or your last settled total.',
  },
  {
    question: 'What is a Matchday?',
    answer: `A Matchday is a numbered period (1–${MATCHDAY_COUNT} for this World Cup) that can include several matches. Your teams earn points from every result in each Matchday that counts for you. This system is designed to mirror Gameweeks from FPL.`,
  },
  {
    question: 'Can I still make a list after the tournament starts?',
    answer:
      'Yes, when late submissions are open. You start on 0 points and only earn from games played on days after you submit. You will be labelled Latecomer in your leagues.',
  },
  {
    question: 'What if I reset my list mid-tournament?',
    answer:
      'During the Group Stage, you may fully reset your list: your score goes to 0, every point earned is permanently forfeited, and you only earn from games on the following day with your new list. You will also be labelled Latecomer in your leagues. The app warns you before you confirm a reset.',
  },
  {
    question: 'What is the team swap?',
    answer:
      'After the Round of 16 ends, a swap window opens. You may pick two positions on your list to swap, and change your mind as often as you like until the window closes. The new order only affects points for the final 7 matches.',
  },
  {
    question: 'How is my score calculated?',
    answer:
      'Each team earns match points when they score goals, win games, and progress (see the Score Points tab for the table). Your list position is a multiplier: 1st = ×10 down to 10th = ×1. Your total is the sum of those weighted points across every game that counts for you.',
  },
  {
    question: 'What do league tags mean?',
    answer:
      'Admin is the league creator. Latecomer means you joined late or reset mid-tournament. An underlined name means that player chose to share their list — tap it to view their 10 teams. A Spectator is a member of a league who didn’t make a list for this tournament.',
  },
];

export const TOURNAMENT_RULES_TIMELINE =
  'Kickoff → Group Stage (late joining and resets allowed) → R32 + R16 → Swap Window → Finals';

export const SCORING_HELP_BULLETS = [
  'Your teams earn points when they score goals, win matches, and progress through the tournament.',
  'Each team’s points are multiplied by its position on your list (1st = ×10 down to 10th = ×1), and your total is the sum of those weighted points across every match that counts.',
  'The number shown is your total so far, counted through the last settled Matchday.',
  'The figure in brackets shows how many points you gained on the most recent settled Matchday.',
  'Points are added when a Matchday is settled; they do not increase during live matches.',
  'If you swapped two teams on your list, your original order applied to Matchdays 1–27; from Matchday 28 onwards the swapped order determines the multipliers. Points already settled are not recalculated.',
] as const;

export const SCORING_HELP_FOOTER =
  'Each Matchday covers several fixtures and is settled as a batch.';

export const RULES_MODAL_TOURNAMENT_SUMMARY = [
  'Points update when a Matchday is settled, not live during matches.',
  'Late join and reset are allowed with penalties (Latecomer label).',
  `Resets are only available through Matchday ${RESET_CUTOFF_MATCHDAY}.`,
] as const;

export type MatchdayScenario = { title: string; body: string };

export const MATCHDAY_SCENARIO_EXAMPLES: MatchdayScenario[] = [
  {
    title: 'Normal play',
    body:
      'Mexico is 1st on your list and scores in Matchday 1. After Matchday 1 is settled, your total updates and (+N) shows what you gained that day.',
  },
  {
    title: 'Late join',
    body:
      'You submit your first list during Matchday 5. Matchday 1–5 do not count for you. You start earning from Matchday 6 onward (once each day is settled).',
  },
  {
    title: 'Reset',
    body: `You had 80 points and reset during Matchday 10. Your score becomes 0, you forfeit everything through Matchday 10, and only earn from Matchday 11 onward with your new list.`,
  },
  {
    title: 'Team swap',
    body: `You swap positions 3 and 7 before the swap window closes. Matchday 1–${SWAP_MATCHDAY} use your original order; from Matchday ${SWAP_MATCHDAY + 1} the swapped multipliers apply.`,
  },
];

export const UPDATED_GENERAL_FAQ: TournamentFaqItem[] = [
  {
    question: 'Do I just rank the 10 teams I think will do best at the World Cup?',
    answer: 'Yes! But there’s a catch…',
  },
  {
    question: "Why can't I choose all the Seeds?",
    answer: 'That would be too easy! You’re only allowed 4 Seeds and you must choose 2 Outsiders.',
  },
  {
    question: 'I’m late, can I still join?',
    answer:
      'Yes, when late submissions are open. You start on 0 points and only earn from games played on days after you submit. You will be labelled Latecomer in your leagues.',
  },
  {
    question: 'Can I change my list once the World Cup begins?',
    answer:
      'If you already have a list you can reset it during the Group Stage. But you will have all previously accumulated points erased and you’ll be labelled as a latecomer',
  },
  {
    question: 'What are leagues?',
    answer:
      'Leagues are where you compete with friends, family, and colleagues — and see how your list stacks up.',
  },
  {
    question: 'Do I have to be a football fanatic?',
    answer: 'Absolutely not. Football is unpredictable and every tournament has upsets. Anyone can win.',
  },
];
